// My Work → Invitations (BA P2.4a, BaSelfGate / ba:profile:self): a Brand
// Ambassador's booking invitations. She reads every frozen term — the fee, the
// paid interval, boarding and return, the adjustment rules, the payment timing
// and the cancellation / no-show terms — then accepts or declines.
//
// Accept sends the termsHash of the terms ON SCREEN (ruling C). If they changed
// meanwhile, commerce answers 409 terms_changed: the invitation is reloaded and
// she is told to read the new terms. Every action is hers alone (human only).
import React, { useCallback, useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { DataState } from '@/features/shared/components/ui/DataState';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  type BaCancellationTerms, type BaInvitation, acceptMyInvitation, declineMyInvitation, errorCode, errorStatus, getMyInvitation,
  listMyInvitations,
} from '@/services/baBookingService';
import { formatInZone, formatMinor } from '../booking/bookingFormat';
import { type K, StatusBadge, useBk, useRunAction } from '../booking/shared';

export const MyInvitationsPage: React.FC = () => {
  const { k } = useBk();
  const [state, setState] = useState<{ s: 'loading' } | { s: 'ok'; rows: BaInvitation[] } | { s: 'unavailable' } | { s: 'error'; msg: string }>({ s: 'loading' });
  const [selected, setSelected] = useState<BaInvitation | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    listMyInvitations()
      .then((rows) => setState({ s: 'ok', rows }))
      .catch((e) => setState(errorStatus(e) === 404 ? { s: 'unavailable' }
        : { s: 'error', msg: getApiErrorMessage(e, k('my.errors_load', 'Could not load your invitations.')) }));
  }, [k]);
  useEffect(() => { setState({ s: 'loading' }); load(); }, [load]);

  const open = (id: string, why: string | null = null) => getMyInvitation(id)
    .then((i) => { setSelected(i); setNotice(why); })
    .catch((e) => setState({ s: 'error', msg: getApiErrorMessage(e, k('my.errors_open', 'Could not open this invitation.')) }));

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="my-invitations-page">
      <div>
        <h1 className="text-h2 text-foreground">{k('my.title', 'Invitations')}</h1>
        <p className="text-sm text-muted-foreground">{k('my.subtitle', 'Events you’re invited to work. Read the terms, then accept or decline.')}</p>
      </div>
      {state.s === 'loading' && <Card><CardContent><DataState variant="loading" /></CardContent></Card>}
      {state.s === 'unavailable' && <Card><CardContent><DataState variant="unavailable" message={k('my.unavailable', 'Booking invitations aren’t available for you.')} /></CardContent></Card>}
      {state.s === 'error' && <Card><CardContent><DataState variant="error" message={state.msg} onRetry={load} /></CardContent></Card>}
      {state.s === 'ok' && (
        <div className="grid gap-4 lg:grid-cols-[22rem_1fr]">
          <Card><CardContent className="p-0">
            {state.rows.length === 0
              ? <DataState variant="empty" title={k('my.empty', 'No invitations yet.')} />
              : (
                <ul>{state.rows.map((i) => (
                  <li key={i.id}>
                    <button type="button" onClick={() => open(i.id)}
                      className={`w-full text-left px-4 py-3 border-b border-border hover:bg-muted ${selected?.id === i.id ? 'bg-muted' : ''}`}>
                      <p className="text-sm font-medium text-foreground">{i.eventTitle}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <StatusBadge status={i.status} k={k} />
                        <span className="text-xs text-muted-foreground">{formatInZone(i.boardingAt, i.timezone)}</span>
                      </div>
                    </button>
                  </li>
                ))}</ul>
              )}
          </CardContent></Card>
          {selected
            ? <InvitationDetail key={`${selected.id}:${selected.version}:${selected.termsHash}`} inv={selected} k={k} notice={notice}
                onReload={(why) => { open(selected.id, why); load(); }} />
            : <Card><CardContent><DataState variant="empty" title={k('my.pick', 'Pick an invitation to read its terms.')} /></CardContent></Card>}
        </div>
      )}
    </div>
  );
};

const portion = (t: BaCancellationTerms, k: K) => {
  if (t.lateCancelBaShareBasis == null || t.lateCancelBaShareValue == null) return '—';
  return t.lateCancelBaShareBasis === 'percent'
    ? k('my.portion_percent', '{{value}}% of the late cancellation charge', { value: t.lateCancelBaShareValue })
    // A fixed portion is minor units of the terms' currency: shown like the fee.
    : formatMinor(t.lateCancelBaShareValue, t.currency);
};

export const InvitationDetail: React.FC<{ inv: BaInvitation; k: K; notice: string | null; onReload: (why: string | null) => void }> = ({
  inv: i, k, notice, onReload,
}) => {
  const confirm = useConfirm();
  const { busy, run } = useRunAction(k);
  const tz = i.timezone;

  const accept = async () => {
    const out = await run(
      (key) => acceptMyInvitation(i.id, i.termsHash, { version: i.version, key }),
      k('my.accepted', 'You accepted. Your time is held for this event.'),
      (e) => {
        const code = errorCode(e);
        if (errorStatus(e) === 409 && code === 'terms_changed') {
          onReload(k('my.terms_changed', 'The terms of this invitation changed. Read them again before you accept.'));
          return true;
        }
        if (errorStatus(e) === 409 && (code === 'invitation_expired' || code === 'stale_version')) {
          onReload(getApiErrorMessage(e));
          return true;
        }
        return false;
      },
    );
    if (out) onReload(null);
  };
  const decline = async () => {
    const ok = await confirm({
      title: k('my.decline_title', 'Decline this invitation'),
      message: k('my.decline_message', 'Decline “{{title}}”? The coordinator is told and may invite someone else.', { title: i.eventTitle }),
      confirmLabel: k('my.decline', 'Decline'), danger: true,
    });
    if (!ok) return;
    if (await run((key) => declineMyInvitation(i.id, { version: i.version, key }), k('my.declined', 'Invitation declined.'))) onReload(null);
  };

  const row = (label: string, value: React.ReactNode, testid?: string) => (
    <div data-testid={testid}><dt className="text-muted-foreground">{label}</dt><dd className="text-foreground whitespace-pre-line">{value || '—'}</dd></div>
  );
  const t = i.cancellationTerms;

  return (
    <Card data-testid="invitation-detail">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>{i.eventTitle}</CardTitle>
          <StatusBadge status={i.status} k={k} />
        </div>
        <p className="text-sm text-muted-foreground">{i.venue} · {i.role}</p>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {notice && <p className="rounded-md bg-warning-soft text-warning-strong p-3" role="alert" data-testid="terms-notice">{notice}</p>}
        <dl className="grid gap-x-4 gap-y-2 md:grid-cols-2">
          {row(k('my.fee', 'Your fee for this shift'), formatMinor(i.feeAmountMinor, i.currency), 'term-fee')}
          {row(k('my.paid_interval', 'Paid interval'), `${formatInZone(i.paidStartAt, tz)} – ${formatInZone(i.paidEndAt, tz)}`, 'term-paid')}
          {row(k('my.boarding', 'Be at boarding by'), formatInZone(i.boardingAt, tz), 'term-boarding')}
          {row(k('my.return', 'Return'), formatInZone(i.returnAt, tz), 'term-return')}
          {row(k('my.adjustment_rules', 'Adjustment rules'), i.adjustmentRules, 'term-adjustment')}
          {row(k('my.payment_timing', 'When you are paid'), i.paymentTiming, 'term-payment')}
          {row(k('my.duties', 'Duties'), i.duties)}
          {row(k('my.expires', 'Reply by'), formatInZone(i.expiresAt, tz))}
        </dl>
        <p className="text-xs text-muted-foreground">{k('my.times_in', 'Times are in {{tz}}.', { tz })}</p>
        <p className="text-xs text-muted-foreground">{k('slots.training', 'Training: not checked in the pilot')}</p>

        <div className="space-y-2" data-testid="cancellation-terms">
          <h3 className="text-sm font-semibold text-foreground">{k('my.cancellation_title', 'Cancellation and no-show terms')}</h3>
          {t ? (
            <dl className="grid gap-x-4 gap-y-2 md:grid-cols-2">
              {row(k('my.terms_title', 'Terms'), t.title)}
              {row(k('my.portion', 'Your portion if the client cancels late'), portion(t, k), 'term-portion')}
              {row(k('my.if_you_cancel', 'If you cancel'), t.baCancellationConsequence)}
              {row(k('my.no_show', 'No-show'), t.noShowTerms)}
              {row(k('my.late_early', 'Late, early or delayed departure'), t.lateEarlyDelayedTerms)}
            </dl>
          ) : <p className="text-muted-foreground">{k('my.no_terms', 'No cancellation and no-show terms are attached to this invitation yet.')}</p>}
        </div>

        {i.status === 'invited' && (
          <div className="flex flex-wrap gap-2">
            <Button disabled={busy} onClick={accept} data-testid="accept-invitation"><Check className="w-4 h-4 mr-1" />{k('my.accept', 'Accept these terms')}</Button>
            <Button variant="outline" disabled={busy} onClick={decline}><X className="w-4 h-4 mr-1" />{k('my.decline', 'Decline')}</Button>
          </div>
        )}
        {i.status === 'accepted' && i.acceptedAt && (
          <p className="text-success-strong">{k('my.accepted_at', 'You accepted on {{at}}.', { at: formatInZone(i.acceptedAt, tz) })}</p>
        )}
      </CardContent>
    </Card>
  );
};

export default MyInvitationsPage;
